/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Alert, Button, Card, FieldGroup, Input, Table } from "core/components";
import { api, ApiError } from "core/api";
import formatter from "core/formatter";
import moment from "moment";
import { Controller, useForm } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { yupResolver } from "@hookform/resolvers/yup";
import { DeliverySchema } from "core/schema";
import { navigateTo } from "core/navigation";
import { pending_site_deliveries_path, print_receiving_slip_site_delivery_path, site_deliveries_path, site_purchase_order_path } from "routes";

type DeliveryFormProps = {
  delivery: Record<string, unknown>;
  can_receive: boolean;
};

const DeliveryForm = ({ delivery, can_receive }: DeliveryFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { isSubmitting, isValidating, errors }
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(DeliverySchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [showReceiveDeliveryConfirmationModal, setShowReceiveDeliveryConfirmationModal] = React.useState(false);
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [modalErrors, setModalErrors] = React.useState<Array<string>>([]);
  const deliveryItems = delivery.delivery_items.filter((delivery_item) => !delivery_item.complete);
  const reorderingColumns = deliveryItems[0]?.project_code ? [] : [2];
  const [isLoading, setIsLoading] = React.useState(false);
  const table = React.useRef(null);
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "Inventory Item",
        accessor: "item",
        Cell: (props) => {
          setValue(`delivery_items.${props.row.index}.purchase_order_item_id`, props.row.original.purchase_order_item_id);
          setValue(`delivery_items.${props.row.index}.allow_partial_delivery`, delivery.allow_partial_delivery);

          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        id: 2,
        Header: "Project ID",
        accessor: "project_code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Purchase Request",
        accessor: "purchase_request_code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Received Quantity",
        accessor: "received_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Delivered Quantity",
        accessor: "delivered_quantity",
        Cell: (props) => {
          const max = +(+props.row.original.ordered_quantity - +props.row.original.received_quantity)
            .toLocaleString("en-PH", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 2
            })
            .replaceAll(",", "");
          return (
            <Controller
              name={`delivery_items.${props.row.index}.delivered_quantity`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FieldGroup errorMessage={error?.message}>
                  <IMaskInput
                    mask={Number}
                    padFractionalZeros={true}
                    thousandsSeparator=","
                    radix="."
                    value={field.value?.toString()}
                    unmask={true}
                    inputRef={field.ref}
                    onAccept={(value) => {
                      field.onChange(value);
                    }}
                    className="field"
                    placeholder="0"
                    min={0}
                    max={max}
                    style={{ textAlign: "center" }}
                    readOnly={!can_receive}
                  />
                </FieldGroup>
              )}
            />
          );
        }
      }
    ],
    []
  );

  React.useEffect(() => {
    setValue("purchase_order_id", delivery.purchase_order.id);
    setValue("allow_partial_delivery", delivery.allow_partial_delivery);
    setValue("delivery_items", deliveryItems);
  }, []);

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={deliveryItems} hiddenColumns={[...reorderingColumns]} ref={table} skipPageReset={true} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return (
      <>
        <Row className="mb-4">
          <Col>
            <Row className="mb-3 align-items-center">
              <Col lg={6}>
                <Row>
                  <Col>
                    <h5>Purchase Order:</h5>
                  </Col>
                  <Col>{delivery.purchase_order.code}</Col>
                </Row>
              </Col>
              <Col lg={6}>
                <Row>
                  <Col>
                    <h5>Warehouse:</h5>
                  </Col>
                  <Col>{delivery.warehouse.name}</Col>
                </Row>
              </Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Ordered By:</h5>
              </Col>
              <Col>{delivery.ordered_by}</Col>
            </Row>
            <Row className="align-items-center">
              <Col lg={3}>
                <h5>Delivered By:</h5>
              </Col>
              <Col>
                <Input
                  {...register("delivered_by")}
                  errorMessage={errors?.delivered_by?.message}
                  placeholder="John Doe"
                  containerProps={{ style: { marginBottom: "1rem" } }}
                  readOnly={!can_receive}
                />
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col lg={3}>
                <h5>Received By:</h5>
              </Col>
              <Col>
                <Input
                  {...register("received_by")}
                  errorMessage={errors?.received_by?.message}
                  placeholder="Jane Doe"
                  containerProps={{ style: { marginBottom: "1rem" } }}
                  readOnly={!can_receive}
                />
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col lg={3}>
                <h5>Remarks:</h5>
              </Col>
              <Col>
                <Controller
                  name="remarks"
                  control={control}
                  render={({ field }) => <textarea {...field} className="field mb-4" placeholder="Remarks" maxLength={250} readOnly={!can_receive} />}
                />
              </Col>
            </Row>
          </Col>
          <Col>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Delivery Date:</h5>
              </Col>
              <Col>{moment().format("LL")}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Allow Partial Delivery:</h5>
              </Col>
              <Col>{delivery.allow_partial_delivery ? "Yes" : "No"}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Supplier:</h5>
              </Col>
              <Col>{delivery.supplier}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Total Items:</h5>
              </Col>
              <Col>{delivery.total_items}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Subtotal Amount:</h5>
              </Col>
              <Col>{formatter.format(parseFloat(delivery.purchase_order.subtotal_amount))}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Value Added Tax:</h5>
              </Col>
              <Col>{formatter.format(parseFloat(delivery.purchase_order.value_added_tax_amount))}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Creditable Withholding Tax:</h5>
              </Col>
              <Col>{`(${formatter.format(parseFloat(delivery.purchase_order.withholding_tax_amount))})`}</Col>
            </Row>
            <Row className="mb-3 align-items-center">
              <Col lg={3}>
                <h5>Total Amount:</h5>
              </Col>
              <Col>{formatter.format(parseFloat(delivery.purchase_order.total_amount))}</Col>
            </Row>
          </Col>
        </Row>
        {dataTable()}
      </>
    );
  };

  const save = async (data) => {
    try {
      setIsLoading(true);
      setFormErrors([]);
      const response = await api.post(site_deliveries_path(), {
        delivery: data
      });
      if (!response.data.subdivision_id) window.open(print_receiving_slip_site_delivery_path(response.data.delivery.id));
      navigateTo(pending_site_deliveries_path());
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.all().find((e) => e.includes("already closed") || e.includes("completely delivered"))) {
          setModalErrors(error.all());
          setShowErrorModal(!showErrorModal);
        } else setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setIsLoading(false);
      setShowReceiveDeliveryConfirmationModal(!showReceiveDeliveryConfirmationModal);
    }
  };

  const onError = (data) => {
    console.error(data);

    if (data.delivery_items?.type == "sum") {
      setFormErrors([data.delivery_items?.message]);
    }
  };

  return (
    <React.StrictMode>
      {formErrors && formErrors.length != 0 && (
        <Alert>
          {formErrors.map((error, index) => {
            return <div key={index}>{error}</div>;
          })}
        </Alert>
      )}
      <Container fluid={true}>
        <Row>
          <Col>
            <Card>
              <Card.Header title={`Delivery`}>
                <Button
                  title="Back"
                  className="button--default"
                  icon="arrow-left"
                  disabled={isSubmitting || isValidating}
                  onClick={() => {
                    navigateTo(site_purchase_order_path(delivery.purchase_order.id));
                  }}
                />
                {can_receive && (
                  <Button
                    title="Receive Delivery"
                    className="button--brand ml-3"
                    icon="clipboard-list"
                    disabled={isLoading}
                    onClick={() => {
                      setShowReceiveDeliveryConfirmationModal(!showReceiveDeliveryConfirmationModal);
                    }}
                  />
                )}
              </Card.Header>
              <Card.Body>{content()}</Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal dialogClassName="modal-dialog-centered" show={showReceiveDeliveryConfirmationModal}>
        <Modal.Header>{`Receive Delivery: ${delivery.purchase_order.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to receive delivery for purchase order: ${delivery.purchase_order.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowReceiveDeliveryConfirmationModal(!showReceiveDeliveryConfirmationModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--success"
            icon="save"
            title="Yes, receive delivery."
            disabled={isLoading}
            onClick={() => {
              handleSubmit(
                async (data) => await save(data),
                (data) => {
                  setShowReceiveDeliveryConfirmationModal(!showReceiveDeliveryConfirmationModal);
                  onError(data);
                }
              )();
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showErrorModal}>
        <Modal.Header>Error encountered</Modal.Header>
        <Modal.Body>
          {modalErrors && modalErrors.length != 0 && (
            <Alert>
              {modalErrors.map((error, index) => {
                return <div key={index}>{error}</div>;
              })}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowErrorModal(!showErrorModal)} icon="times" title="Close" />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default DeliveryForm;
